import { Mail } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SUPPORT_EMAIL, TELEGRAM_SUPPORT_URL, WHATSAPP_SUPPORT_URL } from "@/config/site";

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M9.04 15.9l-.38 5.36c.55 0 .79-.24 1.08-.53l2.6-2.48 5.38 3.94c.99.55 1.7.26 1.97-.91L23.9 4.6c.32-1.45-.52-2.02-1.48-1.66L1.7 11.05c-1.41.55-1.39 1.34-.24 1.7l5.3 1.65L18.98 6.3c.58-.38 1.1-.17.67.21L9.04 15.9z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-3-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.5.1-.2.2-.6.8-.7.9-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.6-1.2.1-.2 0-.4 0-.5l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.5 1.1 2.7c.1.2 1.8 2.8 4.4 3.9.6.3 1.1.4 1.5.6.6.2 1.2.2 1.6.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.2-.2-.5-.3z" />
  </svg>
);

export default function Contact() {
  return (
    <div className="vault-container max-w-2xl pt-14 sm:pt-16" data-testid="contact-page">
      <Reveal>
        <p className="overline-label">Contact</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tighter sm:text-4xl">
          We're here to help.
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          We typically respond as soon as possible.
        </p>
      </Reveal>

      <div className="mt-10 flex flex-col gap-4">
        <Reveal delay={0.06}>
          <div className="flex items-center gap-5 rounded-2xl border border-white/[0.08] bg-card p-6">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04]">
              <Mail className="h-5 w-5 text-primary" />
            </span>
            <div>
              <p className="text-sm font-medium">Support email</p>
              <p className="mt-0.5 text-sm text-muted-foreground" data-testid="contact-email">
                {SUPPORT_EMAIL}
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="flex items-center gap-5 rounded-2xl border border-white/[0.08] bg-card p-6">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white">
              <WhatsAppIcon />
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium">WhatsApp support</p>
              <p className="mt-0.5 text-sm text-muted-foreground">Message us on WhatsApp</p>
            </div>
            {WHATSAPP_SUPPORT_URL ? (
              <a
                data-testid="contact-whatsapp-button"
                href={WHATSAPP_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-medium text-white transition-transform duration-200 hover:scale-[1.03]"
              >
                Open chat
              </a>
            ) : (
              <span
                data-testid="contact-whatsapp-button"
                className="cursor-not-allowed rounded-full bg-[#25D366]/40 px-5 py-2.5 text-sm font-medium text-white/70"
              >
                Coming soon
              </span>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="flex items-center gap-5 rounded-2xl border border-white/[0.08] bg-card p-6">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0088CC] text-white">
              <TelegramIcon />
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium">Telegram support</p>
              <p className="mt-0.5 text-sm text-muted-foreground">Chat with support</p>
            </div>
            {TELEGRAM_SUPPORT_URL ? (
              <a
                data-testid="contact-telegram-button"
                href={TELEGRAM_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-[#0088CC] px-5 py-2.5 text-sm font-medium text-white transition-transform duration-200 hover:scale-[1.03]"
              >
                Open chat
              </a>
            ) : (
              <span
                data-testid="contact-telegram-button"
                className="cursor-not-allowed rounded-full bg-[#0088CC]/40 px-5 py-2.5 text-sm font-medium text-white/70"
              >
                Coming soon
              </span>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
